<script>
import { GlBadge, GlLink, GlSprintf } from '@gitlab/ui';
import { s__ } from '~/locale';
import { SEVERITY_LEVELS_GRAPHQL } from 'ee/security_dashboard/store/constants';
import SeverityBadge from 'ee/vue_shared/security_reports/components/severity_badge.vue';
import FalsePositiveAlert from 'ee/vulnerabilities/components/false_positive_alert.vue';
import GenericReportSection from 'ee/vulnerabilities/components/generic_report/report_section_graphql.vue';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { REPORT_TYPES_TO_HUMAN_READABLE } from './constants';
import DetailsSection from './details_section.vue';
import DetailsSectionListItem from './details_section_list_item.vue';

export default {
  directives: { SafeHtml },
  i18n: {
    descriptionSectionHeading: s__('Vulnerability|Description'),
    severityLabel: s__('Vulnerability|Severity:'),
    stateLabel: s__('Vulnerability|Status:'),
    projectLabel: s__('Vulnerability|Project:'),
    toolLabel: s__('Vulnerability|Tool:'),
    scannerLabel: s__('Vulnerability|Scanner:'),
    locationHeading: s__('Vulnerability|Location'),
    locationFileLabel: s__('Vulnerability|File:'),
    linksSectionHeading: s__('Vulnerability|Links'),
    identifiersSectionHeading: s__('Vulnerability|Identifiers'),
    assetsSectionHeading: s__('Vulnerability|Reproduction Assets'),
  },
  components: {
    GlBadge,
    GlLink,
    GlSprintf,
    SeverityBadge,
    DetailsSection,
    DetailsSectionListItem,
    FalsePositiveAlert,
    GenericReportSection,
  },
  props: {
    /**
     * Description of the vulnerability finding.
     *
     * @type string
     */
    description: {
      type: String,
      required: true,
    },
    /**
     * Description of the vulnerability finding in HTML.
     *
     * @type string
     */
    descriptionHtml: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * The state of the vulnerability
     *
     * @typedef {'CONFIRMED'|'DETECTED'|'DISMISSED'|'RESOLVED'} VulnerabilityState
     * @type VulnerabilityState
     */
    state: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Severity of the vulnerability finding.
     *
     * Possible values: 'INFO', 'UNKNOWN', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'
     *
     * @typedef {('INFO'|'UNKNOWN'|'LOW'|'MEDIUM'|'HIGH'|'CRITICAL')} Severity
     * @type Severity
     */
    severity: {
      type: String,
      required: true,
      validator: (v) => SEVERITY_LEVELS_GRAPHQL.includes(v),
    },
    /**
     * Project on which the vulnerability finding was found.
     *
     * @typedef {{ name: string, webUrl: string }} Project
     * @type Project
     */
    project: {
      type: Object,
      required: true,
    },
    /**
     * Location metadata for the vulnerability. Its fields depend on the type of security scan that found the vulnerability.
     *
     * @typedef {{ startLine?: string, endLine?: string, blobPath?: string, file?: string }} VulnerabilityLocationSast
     * @typedef {( VulnerabilityLocationSast | null )} VulnerabilityLocation
     * @type VulnerabilityLocation
     */
    location: {
      type: Object,
      required: false,
      default: null,
    },
    /**
     * Type of the security report that found the vulnerability finding.
     *
     * @typedef {('SAST' | 'DEPENDENCY_SCANNING' | 'CONTAINER_SCANNING' | 'DAST' | 'SECRET_DETECTION' | 'COVERAGE_FUZZING' | 'API_FUZZING' | 'CLUSTER_IMAGE_SCANNING' | 'GENERIC')} VulnerabilityReportType
     * @type VulnerabilityReportType
     */
    reportType: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * List of links associated with the vulnerability.
     *
     * @typedef {{ name?: string, url: string }} VulnerabilityLink
     * @type {VulnerabilityLink[]}
     */
    links: {
      type: Array,
      required: false,
      default: () => [],
    },
    /**
     * Identifiers of the vulnerability finding.
     *
     * @typedef {{ name: string, url?: string }} VulnerabilityIdentifier
     * @type {VulnerabilityIdentifier[]}
     */
    identifiers: {
      type: Array,
      required: false,
      default: () => [],
    },
    /**
     * Represents a vulnerability scanner
     *
     * @typedef {{ name: string, version?: string }} VulnerabilityScanner
     * @type VulnerabilityScanner
     */
    scanner: {
      type: Object,
      required: false,
      default: null,
    },
    /**
     * Represents a vulnerability asset type.
     *
     * @typedef {{ name: string, url: string }} AssetType
     * @type AssetType[]
     */
    assets: {
      type: Array,
      required: false,
      default: () => [],
    },
    /**
     * Whether the vulnerability is a false positive.
     *
     * @type Boolean
     */
    falsePositive: {
      type: Boolean,
      required: true,
    },
    /**
     * Details of the security finding (Generic report)
     *
     * @typedef {{ type: string }} GraphQLTypenameAlias
     * @typedef {{ url: string, href: string, name: string } &  GraphQLTypenameAlias } VulnerabilityDetailsUrl
     * @typedef {(VulnerabilityDetailsUrl)} VulnerabilityDetails
     *
     * @type VulnerabilityDetails[]
     */
    details: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  computed: {
    humanReadableReportType() {
      return REPORT_TYPES_TO_HUMAN_READABLE[this.reportType];
    },
    file() {
      const { startLine, endLine, blobPath, file } = this.location;

      const lineNumber = endLine > startLine ? `${startLine}-${endLine}` : startLine;
      const url = `${blobPath}#L${lineNumber}`;
      const name = `${file}:${lineNumber}`;

      return {
        url,
        name,
      };
    },
  },
};
</script>

<template>
  <article>
    <false-positive-alert v-if="falsePositive" class="gl-mb-4" />

    <details-section
      :heading="$options.i18n.descriptionSectionHeading"
      data-testid="description-section"
    >
      <div v-if="descriptionHtml" v-safe-html="descriptionHtml"></div>
      <p v-else>{{ description }}</p>
    </details-section>

    <details-section data-testid="main-section">
      <template #list>
        <details-section-list-item
          v-if="state"
          :label="$options.i18n.stateLabel"
          data-testid="state-list-item"
        >
          <gl-badge variant="warning" class="text-capitalize">{{ state }}</gl-badge>
        </details-section-list-item>

        <details-section-list-item
          :label="$options.i18n.severityLabel"
          data-testid="severity-list-item"
        >
          <severity-badge class="gl-display-inline-block gl-ml-2" :severity="severity" />
        </details-section-list-item>

        <details-section-list-item
          :label="$options.i18n.projectLabel"
          data-testid="project-list-item"
        >
          <gl-link :href="project.webUrl">{{ project.name }}</gl-link>
        </details-section-list-item>

        <details-section-list-item
          v-if="humanReadableReportType"
          :label="$options.i18n.toolLabel"
          data-testid="report-type-list-item"
        >
          {{ humanReadableReportType }}
        </details-section-list-item>

        <details-section-list-item
          v-if="scanner"
          :label="$options.i18n.scannerLabel"
          data-testid="scanner-list-item"
        >
          <gl-sprintf
            v-if="scanner.version"
            :message="s__('Vulnerability|%{scannerName} (version %{scannerVersion})')"
          >
            <template #scannerName>{{ scanner.name }}</template>
            <template #scannerVersion>{{ scanner.version }}</template>
          </gl-sprintf>
          <template v-else>{{ scanner.name }}</template>
        </details-section-list-item>
      </template>
    </details-section>

    <details-section
      v-if="location"
      :heading="$options.i18n.locationHeading"
      data-testid="location-section"
    >
      <template #list>
        <details-section-list-item
          v-if="location.file"
          :label="$options.i18n.locationFileLabel"
          data-testid="location-file-list-item"
        >
          <gl-link :href="file.url">{{ file.name }}</gl-link>
        </details-section-list-item>
      </template>
    </details-section>

    <details-section
      v-if="links.length"
      :heading="$options.i18n.linksSectionHeading"
      data-testid="links-section"
    >
      <ul class="gl-p-0">
        <li v-for="link in links" :key="link.url" class="gl-list-style-position-inside">
          <gl-link :href="link.url">
            {{ link.name || link.url }}
          </gl-link>
        </li>
      </ul>
    </details-section>

    <details-section
      v-if="identifiers.length"
      :heading="$options.i18n.identifiersSectionHeading"
      data-testid="identifiers-section"
    >
      <ul class="gl-p-0">
        <li
          v-for="identifier in identifiers"
          :key="identifier.name"
          class="gl-list-style-position-inside"
        >
          <gl-link v-if="identifier.url" :href="identifier.url">
            {{ identifier.name }}
          </gl-link>
          <template v-else>{{ identifier.name }}</template>
        </li>
      </ul>
    </details-section>

    <details-section
      v-if="assets.length"
      :heading="$options.i18n.assetsSectionHeading"
      data-testid="assets-section"
    >
      <ul class="gl-p-0">
        <li v-for="asset in assets" :key="asset.name" class="gl-list-style-position-inside">
          <gl-link v-if="asset.url" :href="asset.url">
            {{ asset.name }}
          </gl-link>
          <template v-else>{{ asset.name }}</template>
        </li>
      </ul>
    </details-section>

    <generic-report-section :report-items="details" />
  </article>
</template>
